import React, { useMemo, useState } from 'react';

interface GoldPriceHistoricalChartProps {
  currentPrice: number; // Price per oz
  isLive?: boolean;
  source?: string;
}

// 1 kg = 32.1507 troy ounces
const OZ_TO_KG = 32.1507;

// Historical gold prices per oz (annual averages, USD) - every year from 1960
// 2025 uses current live price, future years will be added dynamically
const HISTORICAL_GOLD_PRICES_OZ: { year: number; price: number | 'live' }[] = [
  { year: 1960, price: 36 }, { year: 1961, price: 35 }, { year: 1962, price: 35 },
  { year: 1963, price: 35 }, { year: 1964, price: 35 }, { year: 1965, price: 35 },
  { year: 1966, price: 35 }, { year: 1967, price: 35 }, { year: 1968, price: 39 },
  { year: 1969, price: 41 }, { year: 1970, price: 36 }, { year: 1971, price: 41 },
  { year: 1972, price: 58 }, { year: 1973, price: 97 }, { year: 1974, price: 154 },
  { year: 1975, price: 161 }, { year: 1976, price: 125 }, { year: 1977, price: 148 },
  { year: 1978, price: 193 }, { year: 1979, price: 306 }, { year: 1980, price: 615 },
  { year: 1981, price: 460 }, { year: 1982, price: 376 }, { year: 1983, price: 424 },
  { year: 1984, price: 361 }, { year: 1985, price: 317 }, { year: 1986, price: 368 },
  { year: 1987, price: 447 }, { year: 1988, price: 437 }, { year: 1989, price: 381 },
  { year: 1990, price: 383 }, { year: 1991, price: 362 }, { year: 1992, price: 344 },
  { year: 1993, price: 360 }, { year: 1994, price: 384 }, { year: 1995, price: 384 },
  { year: 1996, price: 388 }, { year: 1997, price: 331 }, { year: 1998, price: 294 },
  { year: 1999, price: 279 }, { year: 2000, price: 279 }, { year: 2001, price: 271 },
  { year: 2002, price: 310 }, { year: 2003, price: 363 }, { year: 2004, price: 409 },
  { year: 2005, price: 444 }, { year: 2006, price: 603 }, { year: 2007, price: 695 },
  { year: 2008, price: 872 }, { year: 2009, price: 972 }, { year: 2010, price: 1225 },
  { year: 2011, price: 1571 }, { year: 2012, price: 1669 }, { year: 2013, price: 1411 },
  { year: 2014, price: 1266 }, { year: 2015, price: 1160 }, { year: 2016, price: 1251 },
  { year: 2017, price: 1257 }, { year: 2018, price: 1268 }, { year: 2019, price: 1393 },
  { year: 2020, price: 1770 }, { year: 2021, price: 1799 }, { year: 2022, price: 1800 },
  { year: 2023, price: 1940 }, { year: 2024, price: 2300 }, { year: 2025, price: 'live' },
];

export const GoldPriceHistoricalChart: React.FC<GoldPriceHistoricalChartProps> = ({
  currentPrice,
  isLive = false,
  source = 'Market Data'
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedDecade, setSelectedDecade] = useState<string>('all');

  // Get current year for decade filtering
  const currentYear = new Date().getFullYear();

  // Convert to KG prices - entries with 'live' use the current price
  const allData = useMemo(() => {
    return HISTORICAL_GOLD_PRICES_OZ.map(d => ({
      year: d.year,
      pricePerKg: Math.round((d.price === 'live' ? currentPrice : d.price) * OZ_TO_KG)
    }));
  }, [currentPrice]);

  const currentPricePerKg = Math.round(currentPrice * OZ_TO_KG);
  const startPricePerKg = allData[0]?.pricePerKg || 1157;
  const priceChange = currentPricePerKg - startPricePerKg;
  const percentChange = startPricePerKg > 0 ? (priceChange / startPricePerKg) * 100 : 0;

  const minPrice = Math.min(...allData.map(d => d.pricePerKg));
  const maxPrice = Math.max(...allData.map(d => d.pricePerKg));
  const priceRange = maxPrice - minPrice;

  // Generate decades dynamically based on current year
  const decades = useMemo(() => {
    const decadesList = ['all', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];
    // Add 2030s if we're in 2030 or later
    if (currentYear >= 2030) {
      decadesList.push('2030s');
    }
    return decadesList;
  }, [currentYear]);

  const filteredData = useMemo(() => {
    if (selectedDecade === 'all') return allData;
    const decadeStart = parseInt(selectedDecade);
    // For current decade, include all years up to and including current year
    const decadeEnd = decadeStart + 10;
    return allData.filter(d => d.year >= decadeStart && d.year < decadeEnd);
  }, [allData, selectedDecade]);

  // Chart dimensions
  const width = 700;
  const height = 200;
  const paddingX = 50;
  const paddingY = 30;

  // Generate SVG path
  const chartPath = useMemo(() => {
    if (allData.length === 0) return '';
    const points = allData.map((d, i) => {
      const x = paddingX + (i / (allData.length - 1)) * (width - 2 * paddingX);
      const y = height - paddingY - ((d.pricePerKg - minPrice) / (priceRange || 1)) * (height - 2 * paddingY);
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  }, [allData, minPrice, priceRange]);

  const areaPath = useMemo(() => {
    if (!chartPath) return '';
    return `${chartPath} L ${width - paddingX},${height - paddingY} L ${paddingX},${height - paddingY} Z`;
  }, [chartPath]);

  // Get point coordinates for hover
  const getPointCoords = (index: number) => {
    const x = paddingX + (index / (allData.length - 1)) * (width - 2 * paddingX);
    const y = height - paddingY - ((allData[index].pricePerKg - minPrice) / (priceRange || 1)) * (height - 2 * paddingY);
    return { x, y };
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <div className="gold-historical-chart">
      {/* Header */}
      <div className="ghc-header">
        <div className="ghc-title-section">
          <div className="ghc-icon">📊</div>
          <div className="ghc-title-text">
            <h3>Gold Price History</h3>
            <span className="ghc-subtitle">1960 - 2025 • Price per Kilogram</span>
          </div>
          {isLive && <span className="ghc-live-badge">● LIVE</span>}
        </div>
        <div className="ghc-current-stats">
          <div className="ghc-current-price">
            <span className="ghc-label">Current Price</span>
            <span className="ghc-value">{formatPrice(currentPricePerKg)}/kg</span>
          </div>
          <div className="ghc-change positive">
            <span className="ghc-change-icon">↑</span>
            <span className="ghc-change-text">
              {formatPrice(priceChange)} (+{percentChange.toFixed(0)}%)
            </span>
            <span className="ghc-since">since 1960</span>
          </div>
        </div>
      </div>

      {/* Interactive Chart */}
      <div className="ghc-chart-wrapper">
        <svg viewBox={`0 0 ${width} ${height}`} className="ghc-svg" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(pct => {
            const y = height - paddingY - (pct / 100) * (height - 2 * paddingY);
            const price = minPrice + (pct / 100) * priceRange;
            return (
              <g key={pct}>
                <line x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="#374151" strokeWidth="1" strokeDasharray="4,4" />
                <text x={paddingX - 8} y={y + 4} fill="#6B7280" fontSize="10" textAnchor="end">
                  {formatPrice(price)}
                </text>
              </g>
            );
          })}
          {/* Area fill */}
          <path d={areaPath} fill="url(#goldGradient)" />
          {/* Line */}
          <path d={chartPath} fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" />
          {/* Interactive points */}
          {allData.map((d, i) => {
            const { x, y } = getPointCoords(i);
            return (
              <g key={d.year}
                 onMouseEnter={() => setHoveredIndex(i)}
                 onMouseLeave={() => setHoveredIndex(null)}
                 style={{ cursor: 'pointer' }}>
                <circle cx={x} cy={y} r={hoveredIndex === i ? 8 : 4}
                        fill={hoveredIndex === i ? '#F59E0B' : 'transparent'}
                        stroke={hoveredIndex === i ? '#FFF' : 'transparent'} strokeWidth="2" />
                <circle cx={x} cy={y} r={15} fill="transparent" />
              </g>
            );
          })}
          {/* Hover tooltip */}
          {hoveredIndex !== null && (
            <g>
              <rect x={getPointCoords(hoveredIndex).x - 55} y={getPointCoords(hoveredIndex).y - 50}
                    width="110" height="40" rx="6" fill="#1F2937" stroke="#F59E0B" strokeWidth="1" />
              <text x={getPointCoords(hoveredIndex).x} y={getPointCoords(hoveredIndex).y - 35}
                    fill="#F59E0B" fontSize="14" fontWeight="bold" textAnchor="middle">
                {allData[hoveredIndex].year}
              </text>
              <text x={getPointCoords(hoveredIndex).x} y={getPointCoords(hoveredIndex).y - 18}
                    fill="#FFF" fontSize="12" textAnchor="middle">
                {formatPrice(allData[hoveredIndex].pricePerKg)}/kg
              </text>
            </g>
          )}
          {/* X-axis labels */}
          {[1960, 1970, 1980, 1990, 2000, 2010, 2020, 2025].map(year => {
            const idx = allData.findIndex(d => d.year === year);
            if (idx === -1) return null;
            const x = paddingX + (idx / (allData.length - 1)) * (width - 2 * paddingX);
            return (
              <text key={year} x={x} y={height - 8} fill="#9CA3AF" fontSize="11" textAnchor="middle">
                {year}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Decade Filter */}
      <div className="ghc-decade-filter">
        {decades.map(decade => (
          <button key={decade} className={`ghc-decade-btn ${selectedDecade === decade ? 'active' : ''}`}
                  onClick={() => setSelectedDecade(decade)}>
            {decade === 'all' ? 'All Years' : decade}
          </button>
        ))}
      </div>

      {/* Price Table */}
      <div className="ghc-price-table-wrapper">
        <table className="ghc-price-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Price/kg</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((d, i) => {
              const prevPrice = i > 0 ? filteredData[i - 1].pricePerKg : d.pricePerKg;
              const change = d.pricePerKg - prevPrice;
              const changePct = prevPrice > 0 ? (change / prevPrice) * 100 : 0;
              return (
                <tr key={d.year} className={d.year === 2025 ? 'current-year' : ''}>
                  <td className="year-cell">{d.year}</td>
                  <td className="price-cell">{formatPrice(d.pricePerKg)}</td>
                  <td className={`change-cell ${change >= 0 ? 'positive' : 'negative'}`}>
                    {i > 0 && (
                      <>
                        <span>{change >= 0 ? '↑' : '↓'}</span>
                        <span>{changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}%</span>
                      </>
                    )}
                    {i === 0 && <span className="baseline">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="ghc-footer">
        <span className="ghc-source">Source: {source}</span>
        <span className="ghc-note">Historical annual averages • Hover over chart for details</span>
      </div>
    </div>
  );
};

export default GoldPriceHistoricalChart;

